// components/Clinician/Imaging.jsx
// Imaging management dashboard with high-fidelity UI matching premium mockups.
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  FormControl,
  InputAdornment,
  Tabs,
  Tab,
  Portal,
  Popover,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search,
  CalendarMonth,
  FilterList,
  ViewList,
  Download,
  VisibilityOutlined,
  FileUpload,
  Refresh,
  CloudUpload,
  WarningAmber,
  Storage,
  TaskAlt,
  AccessTime,
  ImageNotSupported,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { getScanBoardStudies, syncOrthanc } from "../../services/pacs";
import api from "../../services/api";
import { calculateAge, generateOHIFUrl } from "../../services/utils";
import EnhancedUploadDialog from "./EnhancedUploadDialog";
import { IMAGING_STATUS_STYLES, resolveImagingStatus } from "./imagingStatus";
import { exportToCSV } from "../../utils/exportUtils";
import ColumnSelectorPopover from "../Common/ColumnSelectorPopover";

const IMAGING_COLUMNS = [
  { key: 'studyDate', label: 'Study Date' },
  { key: 'patient', label: 'Patient', required: true },
  { key: 'accessionNo', label: 'Accession No.' },
  { key: 'modality', label: 'Modality' },
  { key: 'studyDescription', label: 'Study Description' },
  { key: 'images', label: 'Images' },
  { key: 'status', label: 'Status' },
  { key: 'readingPhysician', label: 'Reading Physician' },
  { key: 'actions', label: 'Actions', required: true },
];

export default function Imaging() {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const textColorPrimary = isDark ? "#F1F5F9" : "#0F172A";
  const textColorSecondary = isDark ? "#CBD5E1" : "#475569";
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const cardBorder = isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #E2E8F0";
  const cardBorderColor = isDark ? "rgba(255,255,255,0.1)" : "#E2E8F0";
  const tableHeaderBg = isDark ? "#18181B" : "#F8FAFC";
  const rowHoverBg = isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC";

  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadAccession, setUploadAccession] = useState("");
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Unified Search & Filters states
  const [searchVal, setSearchVal] = useState("");
  const [dateRange, setDateRange] = useState("All Time");
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [modality, setModality] = useState("All");
  const [studyStatus, setStudyStatus] = useState("All");
  const [bodyPart, setBodyPart] = useState("All");

  // Static lists for filter dropdowns
  const MODALITIES = ["CT", "MRI", "XRAY", "US", "MAMMO", "ANGIO", "BMD"];
  const STATUSES = ["Not Uploaded", "Awaiting Report", "Completed", "Failed"];
  const BODY_PARTS = ["Brain", "Knee", "Chest", "Abdomen", "Spine", "Shoulder"];

  const fetchStudies = useCallback(async () => {
    setLoading(true);
    try {
      try {
        await syncOrthanc();
      } catch (syncErr) {
        console.warn("Orthanc sync skipped:", syncErr?.message);
      }

      const params = { limit: 50, page: 1 };
      if (searchVal) params.search = searchVal;
      if (modality !== "All") params.modality = modality;
      if (dateRange === "This Year") {
        params.year = new Date().getFullYear();
      } else if (dateRange && dateRange.startsWith("Year ")) {
        params.year = dateRange.replace("Year ", "").trim();
      }

      const response = await getScanBoardStudies(params);
      const rawList = response.studies || [];

      const formattedList = rawList.map((s) => {
        const pName = s.patientName ? s.patientName.replace(/\^/g, " ") : "-";
        const pSex = s.patientSex || "-";
        const pAge = s.patientBirthDate
          ? `${calculateAge(s.patientBirthDate)} Y`
          : "-";

        const imageCount = s.totalInstances || s.instanceCount || 0;
        const { key: statusKey, subText } = resolveImagingStatus({
          ...s,
          images: imageCount,
        });

        const studyDateObj = s.studyDate ? new Date(s.studyDate) : new Date();
        const dateStr = studyDateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const timeStr = studyDateObj.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        return {
          ...s,
          dateFormatted: dateStr,
          timeFormatted: timeStr,
          patientName: pName,
          patientSex: pSex,
          patientAge: pAge,
          accessionNo: s.accessionNumber || "-",
          orderNo: s.accessionNumber
            ? `ORD-${s.accessionNumber.split("-")[1] || s.accessionNumber}`
            : "-",
          modalityVal: s.modality || "-",
          procedure: s.studyDescription || "-",
          images: imageCount,
          series: s.seriesCount || 0,
          workflowStatus: statusKey,
          statusSubText: subText,
          readingPhysician:
            s.readingPhysician || s.assignedRadiologistName || "-",
          bodyPart: s.bodyPart || s.bodyPartExamined || "-",
        };
      });

      setStudies(formattedList);
    } catch (err) {
      console.error("Failed to load studies list:", err);
    } finally {
      setLoading(false);
    }
  }, [searchVal, modality, dateRange]);

  const handleUpload = async (formData) => {
    setUploading(true);
    try {
      const response = await api.post("/images/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      await fetchStudies();
      return response.data;
    } finally {
      setUploading(false);
    }
  };

  const openManualUpload = (study) => {
    setUploadAccession(
      study?.accessionNo && study.accessionNo !== "-" ? study.accessionNo : "",
    );
    setShowUpload(true);
  };

  const handleRetrySync = async (study) => {
    setSyncing(true);
    try {
      await syncOrthanc();
      await fetchStudies();
      setSnackbar({
        open: true,
        message: `Sync retried for ${study.accessionNo}`,
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Sync failed",
        severity: "error",
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies]);

  const handleClearFilters = () => {
    setSearchVal("");
    setModality("All");
    setStudyStatus("All");
    setBodyPart("All");
    setPage(0);
  };

  const handleActionClick = async (study, action) => {
    try {
      switch (action) {
        case "View Images": {
          try {
            const studyInstanceUID =
              study.studyInstanceUID || study.studyUID || study.orthancStudyId;
            if (!studyInstanceUID) {
              setSnackbar({
                open: true,
                message: "Study UID is missing. Viewer cannot be opened.",
                severity: "warning",
              });
              break;
            }

            const ohifBaseUrl =
              process.env.REACT_APP_OHIF_URL ||
              window.env?.REACT_APP_OHIF_URL ||
              "http://localhost:8042/ohif";
            const viewerUrl = generateOHIFUrl(studyInstanceUID, ohifBaseUrl);

            console.log("Opening OHIF viewer:", viewerUrl);

            const newWindow = window.open(
              viewerUrl,
              "_blank",
              "noopener,noreferrer,width=1400,height=900,scrollbars=yes,resizable=yes",
            );
            if (newWindow) {
              newWindow.opener = null;
              try {
                newWindow.focus();
              } catch (e) {}
            } else {
              // Popup blocked fallback
              const link = document.createElement("a");
              link.href = viewerUrl;
              link.target = "_blank";
              link.rel = "noopener noreferrer";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          } catch (err) {
            setSnackbar({
              open: true,
              message: "Could not open viewer",
              severity: "error",
            });
          }
          break;
        }
        case "Retry Sync":
          await handleRetrySync(study);
          break;
        case "Upload Images":
          openManualUpload(study);
          break;
        default:
          setSnackbar({
            open: true,
            message: `Action "${action}" triggered for study: ${study.accessionNo}`,
            severity: "info",
          });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed: ${err.response?.data?.error || err.message}`,
        severity: "error",
      });
    }
  };

  // Filter based on dropdown selections and active tab
  const getFilteredList = () => {
    return studies.filter((s) => {
      // Modality filter
      if (modality !== "All" && s.modalityVal !== modality) return false;

      if (
        studyStatus !== "All" &&
        IMAGING_STATUS_STYLES[s.workflowStatus]?.label !== studyStatus
      )
        return false;

      // Body part filter
      if (bodyPart !== "All" && s.bodyPart !== bodyPart) return false;

      // Date range filter
      if (dateRange && dateRange !== "All Time") {
        const d = s.studyDate ? new Date(s.studyDate) : (s.createdAt ? new Date(s.createdAt) : null);
        if (d && !isNaN(d.getTime())) {
          const now = new Date();
          if (dateRange === "Today") {
            if (d.toDateString() !== now.toDateString()) return false;
          } else if (dateRange === "Last 7 Days") {
            const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (d < sevenDaysAgo) return false;
          } else if (dateRange === "This Month") {
            if (d.getFullYear() !== now.getFullYear() || d.getMonth() !== now.getMonth()) return false;
          } else if (dateRange === "This Year") {
            if (d.getFullYear() !== now.getFullYear()) return false;
          } else if (dateRange.startsWith("Year ")) {
            const yr = parseInt(dateRange.replace("Year ", ""), 10);
            if (d.getFullYear() !== yr) return false;
          }
        }
      }

      // Search match (including 4-digit year)
      if (searchVal && searchVal.trim()) {
        const q = searchVal.trim().toLowerCase();
        if (/^\d{4}$/.test(q)) {
          const yr = parseInt(q, 10);
          const d = s.studyDate ? new Date(s.studyDate) : (s.createdAt ? new Date(s.createdAt) : null);
          const matchesYear = d && !isNaN(d.getTime()) && d.getFullYear() === yr;
          const matchesText = (s.patientName || '').toLowerCase().includes(q) ||
            (s.patientId || '').toLowerCase().includes(q) ||
            (s.accessionNo || '').toLowerCase().includes(q) ||
            (s.procedure || '').toLowerCase().includes(q);
          if (!matchesYear && !matchesText) return false;
        }
      }

      // Tab filter
      if (activeTab === 0) return true; // All
      if (activeTab === 1)
        return (
          s.dateFormatted ===
          new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        ); // Today
      if (activeTab === 4) return s.workflowStatus === "AWAITING_REPORT";
      if (activeTab === 5) return s.workflowStatus === "COMPLETED";
      if (activeTab === 6) return s.workflowStatus === "FAILED";
      if (activeTab === 7) return s.workflowStatus === "NOT_UPLOADED";
      return true;
    });
  };

  const filteredList = getFilteredList();

  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('imaging_table_columns');
      return saved ? JSON.parse(saved) : IMAGING_COLUMNS.map(c => c.key);
    } catch {
      return IMAGING_COLUMNS.map(c => c.key);
    }
  });

  const handleToggleColumn = (colKey) => {
    setVisibleColumns(prev => {
      const next = prev.includes(colKey)
        ? prev.filter(k => k !== colKey)
        : [...prev, colKey];
      try {
        localStorage.setItem('imaging_table_columns', JSON.stringify(next));
      } catch (e) {
        console.error(e);
      }
      return next;
    });
  };

  const handleResetColumns = () => {
    const defaults = IMAGING_COLUMNS.map(c => c.key);
    setVisibleColumns(defaults);
    try {
      localStorage.setItem('imaging_table_columns', JSON.stringify(defaults));
    } catch (e) {
      console.error(e);
    }
  };

  const handleExportStudies = () => {
    if (!filteredList || filteredList.length === 0) {
      setSnackbar({ open: true, message: 'No studies to export', severity: 'warning' });
      return;
    }
    const exportData = filteredList.map(s => ({
      'Study Date': s.dateFormatted || '',
      'Study Time': s.timeFormatted || '',
      'Patient Name': s.patientName || '',
      'Patient ID': s.patientId || '',
      'Gender': s.patientSex || '',
      'Age': s.patientAge || '',
      'Accession No': s.accessionNo || '',
      'Order No': s.orderNo || '',
      'Modality': s.modalityVal || '',
      'Study Description': s.procedure || '',
      'Body Part': s.bodyPart || '',
      'Images': s.images || 0,
      'Series': s.series || 0,
      'Status': s.workflowStatus || '',
      'Reading Physician': s.readingPhysician || '',
    }));
    exportToCSV(exportData, `Imaging_Studies_${new Date().toISOString().slice(0, 10)}.csv`);
    setSnackbar({ open: true, message: `Exported ${exportData.length} studies to CSV successfully`, severity: 'success' });
  };

  // Tab counts
  const tabCounts = {
    all: studies.length,
    today: studies.filter(
      (s) =>
        s.dateFormatted ===
        new Date().toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
    ).length,
    thisWeek: studies.length, // Simplified for now since we'd need date logic
    thisMonth: studies.length,
    awaitingReport: studies.filter(
      (s) => s.workflowStatus === "AWAITING_REPORT",
    ).length,
    completed: studies.filter((s) => s.workflowStatus === "COMPLETED").length,
    failed: studies.filter((s) => s.workflowStatus === "FAILED").length,
    notUploaded: studies.filter((s) => s.workflowStatus === "NOT_UPLOADED")
      .length,
  };

  const renderActionsColumn = (study) => {
    const status = study.workflowStatus;
    const needsUpload = status === "FAILED" || status === "NOT_UPLOADED";
    const canView = status === "AWAITING_REPORT" || status === "COMPLETED";

    const actionBtnStyle = {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: "4px",
      height: "24px",
      fontSize: "0.72rem",
      px: 1,
      py: 0,
      whiteSpace: "nowrap",
      justifyContent: "center",
      alignItems: "center",
      "& .MuiButton-startIcon": { mr: 0.5, "& > *:first-of-type": { fontSize: "13px" } },
    };

    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {needsUpload && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUpload />}
              onClick={() => handleActionClick(study, "Upload Images")}
              sx={{
                ...actionBtnStyle,
                borderColor: "#4F46E5",
                color: "#4F46E5",
                "&:hover": { bgcolor: isDark ? "rgba(79,70,229,0.15)" : "#EEF2FF" },
              }}
            >
              Upload Images
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              disabled={syncing}
              onClick={() => handleActionClick(study, "Retry Sync")}
              sx={{
                ...actionBtnStyle,
                borderColor: cardBorderColor,
                color: textColorSecondary,
                "&:hover": { borderColor: "#4F46E5", bgcolor: rowHoverBg },
              }}
            >
              Retry Sync
            </Button>
          </>
        )}
        {canView && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlined />}
            onClick={() => handleActionClick(study, "View Images")}
            sx={{
              ...actionBtnStyle,
              borderColor: cardBorderColor,
              color: textColorSecondary,
              "&:hover": { borderColor: "#4F46E5", bgcolor: rowHoverBg },
            }}
          >
            View Images
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5 }, pb: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: textColorPrimary,
              fontSize: "1.15rem",
              lineHeight: 1.2,
            }}
          >
            Imaging
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: textColorSecondary, fontSize: "0.78rem" }}
          >
            View, manage and analyze all imaging studies.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloudUpload sx={{ fontSize: "15px !important" }} />}
            onClick={() => openManualUpload(null)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              height: "30px",
              px: 1.5,
              color: textColorSecondary,
              borderColor: cardBorderColor,
              borderRadius: "6px",
              bgcolor: cardBg,
              "&:hover": { borderColor: "#4F46E5", bgcolor: rowHoverBg },
            }}
          >
            Upload Images
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileUpload sx={{ fontSize: "15px !important" }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              height: "30px",
              bgcolor: "#4F46E5",
              color: "#FFFFFF",
              borderRadius: "6px",
              "&:hover": { bgcolor: "#4338CA" },
            }}
          >
            Import from Modalities
          </Button>
        </Box>
      </Box>

      {/* Workflow Stats Cards */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "nowrap",
          overflowX: "auto",
          pb: 0.5,
          width: "100%",
          "&::-webkit-scrollbar": { height: "4px" },
        }}
      >
        {[
          {
            label: "Total Studies",
            value: studies.length,
            desc: "All records",
            icon: <Storage />,
            color: "#3B82F6",
            bg: isDark ? "rgba(59,130,246,0.15)" : "#EFF6FF",
          },
          {
            label: "Not Uploaded",
            value: tabCounts.notUploaded,
            desc: "No images",
            icon: <ImageNotSupported />,
            color: textColorSecondary,
            bg: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9",
          },
          {
            label: "Awaiting Report",
            value: tabCounts.awaitingReport,
            desc: "Pending report",
            icon: <AccessTime />,
            color: "#EA580C",
            bg: isDark ? "rgba(234,88,12,0.15)" : "#FFF7ED",
          },
          {
            label: "Completed",
            value: tabCounts.completed,
            desc: "Report done",
            icon: <TaskAlt />,
            color: "#10B981",
            bg: isDark ? "rgba(16,185,129,0.15)" : "#ECFDF5",
          },
          {
            label: "Failed",
            value: tabCounts.failed,
            desc: "Sync issue",
            icon: <WarningAmber />,
            color: "#EF4444",
            bg: isDark ? "rgba(239,68,68,0.15)" : "#FEF2F2",
          },
        ].map((card, idx) => (
          <Paper
            key={idx}
            sx={{
              p: "8px 12px",
              borderRadius: "8px",
              border: cardBorder,
              boxShadow: "none",
              bgcolor: cardBg,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: "140px",
              flex: "1 0 auto",
              transition: "all 0.2s",
              "&:hover": {
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.05)",
                transform: "translateY(-1px)",
              },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "6px",
                bgcolor: card.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: card.color,
                flexShrink: 0,
              }}
            >
              {React.cloneElement(card.icon, { sx: { fontSize: 18 } })}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 800, color: textColorPrimary, fontSize: "1.1rem", lineHeight: 1.1 }}
              >
                {card.value}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: textColorPrimary,
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                }}
              >
                {card.label}
              </Typography>
              <Typography
                sx={{
                  color: textColorSecondary,
                  display: "block",
                  fontSize: "0.65rem",
                }}
              >
                {card.desc}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Filter Section */}
      <Paper
        sx={{
          p: "8px 12px",
          mb: 1.5,
          border: cardBorder,
          boxShadow: "none",
          borderRadius: "8px",
          bgcolor: cardBg,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "flex-end",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              minWidth: "180px",
              flex: "2 1 200px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Search
            </Typography>
            <TextField
              placeholder="Patient name, Accession no..."
              size="small"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "30px",
                  borderRadius: "6px",
                  fontSize: "0.78rem",
                  bgcolor: cardBg,
                  color: textColorPrimary,
                  "& fieldset": { borderColor: cardBorderColor },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search sx={{ fontSize: 16, color: textColorSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              minWidth: "140px",
              flex: "1 1 140px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Date Range
            </Typography>
            <Button
              onClick={(e) => setDateAnchorEl(e.currentTarget)}
              startIcon={<CalendarMonth sx={{ fontSize: 15, color: textColorSecondary }} />}
              sx={{
                borderRadius: "6px",
                border: cardBorder,
                textTransform: "none",
                color: textColorPrimary,
                px: 1.25,
                height: "30px",
                fontSize: "0.78rem",
                fontWeight: 500,
                bgcolor: cardBg,
                justifyContent: "flex-start",
                "&:hover": { borderColor: "#4F46E5", bgcolor: rowHoverBg },
              }}
            >
              {dateRange}
            </Button>
            <Popover
              open={Boolean(dateAnchorEl)}
              anchorEl={dateAnchorEl}
              onClose={() => setDateAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              PaperProps={{
                sx: {
                  p: 1,
                  bgcolor: cardBg,
                  borderRadius: "8px",
                  border: cardBorder,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textColorPrimary, mb: 0.5, px: 1 }}>
                Select Date Range
              </Typography>
              {[
                { label: "Today", value: "Today" },
                { label: "Last 7 Days", value: "Last 7 Days" },
                {
                  label: (() => {
                    const now = new Date();
                    const year = now.getFullYear();
                    const month = now.getMonth();
                    const start = new Date(year, month, 1);
                    const end = new Date(year, month + 1, 0);
                    const fmt = (d) =>
                      `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                    return `This Month (${fmt(start)} - ${fmt(end)})`;
                  })(),
                  value: "This Month",
                },
                {
                  label: `This Year (${new Date().getFullYear()})`,
                  value: "This Year",
                },
                {
                  label: `Year ${new Date().getFullYear() - 1}`,
                  value: `Year ${new Date().getFullYear() - 1}`,
                },
                {
                  label: `Year ${new Date().getFullYear() - 2}`,
                  value: `Year ${new Date().getFullYear() - 2}`,
                },
                { label: "All Time", value: "All Time" },
              ].map((opt) => (
                <MenuItem
                  key={opt.value}
                  onClick={() => {
                    setDateRange(opt.value);
                    setDateAnchorEl(null);
                  }}
                  sx={{
                    fontSize: "0.78rem",
                    py: 0.5,
                    borderRadius: "4px",
                    fontWeight: dateRange === opt.value ? 700 : 400,
                    bgcolor: dateRange === opt.value ? (isDark ? "rgba(79,70,229,0.2)" : "#EFF6FF") : "transparent",
                    color: textColorPrimary,
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Popover>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              minWidth: "110px",
              flex: "1 1 110px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Modality
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={modality}
                onChange={(e) => setModality(e.target.value)}
                sx={{
                  height: "30px",
                  borderRadius: "6px",
                  fontSize: "0.78rem",
                  bgcolor: cardBg,
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                {MODALITIES.map((m) => (
                  <MenuItem key={m} value={m} sx={{ fontSize: "0.78rem" }}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              minWidth: "120px",
              flex: "1 1 120px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Status
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={studyStatus}
                onChange={(e) => setStudyStatus(e.target.value)}
                sx={{
                  height: "30px",
                  borderRadius: "6px",
                  fontSize: "0.78rem",
                  bgcolor: cardBg,
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontSize: "0.78rem" }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              minWidth: "110px",
              flex: "1 1 110px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Body Part
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={bodyPart}
                onChange={(e) => setBodyPart(e.target.value)}
                sx={{
                  height: "30px",
                  borderRadius: "6px",
                  fontSize: "0.78rem",
                  bgcolor: cardBg,
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                {BODY_PARTS.map((part) => (
                  <MenuItem key={part} value={part} sx={{ fontSize: "0.78rem" }}>
                    {part}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 1, shrink: 0 }}>
            <Button
              onClick={handleClearFilters}
              size="small"
              sx={{
                textTransform: "none",
                color: textColorSecondary,
                fontWeight: 600,
                fontSize: "0.78rem",
                height: "30px",
                minWidth: "50px",
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<FilterList sx={{ fontSize: 16 }} />}
              onClick={fetchStudies}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                height: "30px",
                bgcolor: "#4F46E5",
                borderRadius: "6px",
                color: "#FFFFFF",
                minWidth: "80px",
                "&:hover": { bgcolor: "#4338CA" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs & Table Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            minHeight: "34px",
            "& .MuiTabs-indicator": {
              backgroundColor: "#4F46E5",
              height: "2px",
            },
            "& .MuiTab-root": {
              minHeight: "34px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              color: textColorSecondary,
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              "&.Mui-selected": { color: "#4F46E5" },
            },
          }}
        >
          <Tab label={`All Studies (${tabCounts.all})`} />
          <Tab label={`Today (${tabCounts.today})`} />
          <Tab label={`This Week (${tabCounts.thisWeek})`} />
          <Tab label={`This Month (${tabCounts.thisMonth})`} />
          <Tab label={`Awaiting Report (${tabCounts.awaitingReport})`} />
          <Tab label={`Completed (${tabCounts.completed})`} />
          <Tab label={`Failed (${tabCounts.failed})`} />
          <Tab label={`Not Uploaded (${tabCounts.notUploaded})`} />
        </Tabs>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewList sx={{ fontSize: 16 }} />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              height: "30px",
              color: textColorSecondary,
              borderColor: cardBorderColor,
              borderRadius: "6px",
              bgcolor: cardBg,
              "&:hover": { borderColor: "#4F46E5", bgcolor: rowHoverBg },
            }}
          >
            Columns
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download sx={{ fontSize: 16 }} />}
            onClick={handleExportStudies}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              height: "30px",
              color: textColorSecondary,
              borderColor: cardBorderColor,
              borderRadius: "6px",
              bgcolor: cardBg,
              "&:hover": { borderColor: "#4F46E5", bgcolor: rowHoverBg },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Studies Table */}
      <TableContainer
        component={Paper}
        sx={{
          border: cardBorder,
          boxShadow: "none",
          borderRadius: "8px",
          overflowX: "auto",
          bgcolor: cardBg,
        }}
      >
        <Table size="small" sx={{ minWidth: "1100px" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: tableHeaderBg }}>
              {visibleColumns.includes('studyDate') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Study Date
                </TableCell>
              )}
              {visibleColumns.includes('patient') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Patient
                </TableCell>
              )}
              {visibleColumns.includes('accessionNo') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Accession No.
                </TableCell>
              )}
              {visibleColumns.includes('modality') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Modality
                </TableCell>
              )}
              {visibleColumns.includes('studyDescription') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Study Description
                </TableCell>
              )}
              {visibleColumns.includes('images') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Images
                </TableCell>
              )}
              {visibleColumns.includes('status') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Status
                </TableCell>
              )}
              {visibleColumns.includes('readingPhysician') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Reading Physician
                </TableCell>
              )}
              {visibleColumns.includes('actions') && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: textColorSecondary,
                    fontSize: "0.72rem",
                    py: "5px !important",
                    px: "8px !important",
                    textAlign: "right",
                    textTransform: "uppercase",
                    borderColor: cardBorderColor,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length || 9}
                  sx={{ textAlign: "center", py: 4, borderColor: cardBorderColor }}
                >
                  <CircularProgress size={28} sx={{ color: "#4F46E5" }} />
                </TableCell>
              </TableRow>
            ) : filteredList.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length || 9}
                  sx={{
                    textAlign: "center",
                    py: 4,
                    borderColor: cardBorderColor,
                    color: textColorSecondary,
                    fontSize: "0.78rem",
                  }}
                >
                  No studies found
                </TableCell>
              </TableRow>
            ) : (
              filteredList
                .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                .map((study, index) => {
                  const style =
                    IMAGING_STATUS_STYLES[study.workflowStatus] ||
                    IMAGING_STATUS_STYLES.NOT_UPLOADED;

                  return (
                    <TableRow key={study._id || index} hover sx={{ height: "34px", "&:hover": { bgcolor: rowHoverBg } }}>
                      {visibleColumns.includes('studyDate') && (
                        <TableCell sx={{ borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}>
                          <Box sx={{ lineHeight: 1.15 }}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: textColorPrimary,
                                fontWeight: 600,
                              }}
                            >
                              {study.dateFormatted}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.68rem", color: textColorSecondary }}
                            >
                              {study.timeFormatted}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('patient') && (
                        <TableCell sx={{ borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}>
                          <Box sx={{ lineHeight: 1.15 }}>
                            <Typography
                              sx={{
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                color: textColorPrimary,
                              }}
                            >
                              {study.patientName}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.68rem", color: textColorSecondary }}
                            >
                              {study.patientSex}, {study.patientAge}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('accessionNo') && (
                        <TableCell sx={{ borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}>
                          <Box sx={{ lineHeight: 1.15 }}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: textColorPrimary,
                                fontWeight: 600,
                              }}
                            >
                              {study.accessionNo}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.68rem", color: textColorSecondary }}
                            >
                              {study.orderNo}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('modality') && (
                        <TableCell sx={{ borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 0.75,
                              py: 0.2,
                              borderRadius: "4px",
                              border: cardBorder,
                              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#F8FAFC",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                color: textColorSecondary,
                              }}
                            >
                              {study.modalityVal}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('studyDescription') && (
                        <TableCell sx={{ borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}>
                          <Box sx={{ lineHeight: 1.15 }}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: textColorPrimary,
                                fontWeight: 600,
                              }}
                            >
                              {study.procedure}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.68rem", color: textColorSecondary }}
                            >
                              {study.bodyPart}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('images') && (
                        <TableCell sx={{ borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}>
                          <Box sx={{ lineHeight: 1.15 }}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: textColorPrimary,
                                fontWeight: 600,
                              }}
                            >
                              {study.images}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.68rem", color: textColorSecondary }}
                            >
                              Series: {study.series}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('status') && (
                        <TableCell sx={{ borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.2 }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                px: 1,
                                py: 0.2,
                                borderRadius: "4px",
                                fontSize: "0.68rem",
                                fontWeight: 600,
                                bgcolor: isDark ? "rgba(255,255,255,0.08)" : style.bg,
                                color: style.color,
                              }}
                            >
                              {style.label}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: textColorSecondary,
                                lineHeight: 1,
                              }}
                            >
                              {study.statusSubText}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('readingPhysician') && (
                        <TableCell
                          sx={{
                            fontSize: "0.75rem",
                            color: textColorSecondary,
                            fontWeight: 500,
                            borderColor: cardBorderColor,
                            py: "3px !important",
                            px: "8px !important",
                          }}
                        >
                          {study.readingPhysician}
                        </TableCell>
                      )}
                      {visibleColumns.includes('actions') && (
                        <TableCell
                          sx={{ textAlign: "right", borderColor: cardBorderColor, py: "3px !important", px: "8px !important" }}
                        >
                          {renderActionsColumn(study)}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>

        {/* Dynamic Pagination Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "6px 12px",
            borderTop: `1px solid ${cardBorderColor}`,
            flexWrap: "wrap",
            gap: 1.5,
            bgcolor: cardBg,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.75rem" }}
            >
              Showing {filteredList.length > 0 ? page * rowsPerPage + 1 : 0} to{" "}
              {Math.min((page + 1) * rowsPerPage, filteredList.length)} of{" "}
              {filteredList.length} studies
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: textColorSecondary, fontWeight: 600 }}
              >
                Rows:
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                sx={{
                  height: 26,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: textColorPrimary,
                  "& .MuiSelect-select": { py: "2px", px: "6px" },
                  "& fieldset": { borderColor: cardBorderColor },
                }}
              >
                {[10, 20, 25, 50, 100].map((num) => (
                  <MenuItem key={num} value={num} sx={{ fontSize: "0.75rem" }}>{num}</MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <IconButton
              size="small"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              sx={{
                border: cardBorder,
                borderRadius: "6px",
                width: 28,
                height: 28,
                color: textColorSecondary,
                "&:disabled": { color: isDark ? "rgba(255, 255, 255, 0.2)" : "#CBD5E1" }
              }}
            >
              <ChevronLeft sx={{ fontSize: 16 }} />
            </IconButton>

            {(() => {
              const totalPages = Math.ceil(filteredList.length / rowsPerPage) || 1;
              const pageNumbers = [];
              for (let i = 0; i < totalPages; i++) {
                if (i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1) {
                  pageNumbers.push(i);
                } else if ((i === 1 && page > 2) || (i === totalPages - 2 && page < totalPages - 3)) {
                  pageNumbers.push('ellipsis');
                }
              }
              const uniquePageNumbers = pageNumbers.filter((item, index, self) => 
                item !== 'ellipsis' || self.indexOf('ellipsis') === index
              );
              return uniquePageNumbers.map((item, idx) => {
                if (item === 'ellipsis') {
                  return (
                    <Typography key={`ellipsis-${idx}`} variant="caption" sx={{ px: 0.5, color: textColorSecondary }}>
                      ...
                    </Typography>
                  );
                }
                const isActive = item === page;
                return (
                  <Button
                    key={item}
                    size="small"
                    onClick={() => setPage(item)}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      p: 0,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      borderRadius: "6px",
                      bgcolor: isActive ? "#4F46E5" : "transparent",
                      color: isActive ? "#FFFFFF" : textColorSecondary,
                      border: isActive ? "none" : cardBorder,
                      "&:hover": {
                        bgcolor: isActive ? "#4338CA" : rowHoverBg,
                      }
                    }}
                  >
                    {item + 1}
                  </Button>
                );
              });
            })()}

            <IconButton
              size="small"
              disabled={page >= Math.ceil(filteredList.length / rowsPerPage) - 1}
              onClick={() => setPage(page + 1)}
              sx={{
                border: cardBorder,
                borderRadius: "6px",
                width: 28,
                height: 28,
                color: textColorSecondary,
                "&:disabled": { color: isDark ? "rgba(255, 255, 255, 0.2)" : "#CBD5E1" }
              }}
            >
              <ChevronRight sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </TableContainer>

      <Portal>
        <EnhancedUploadDialog
          open={showUpload}
          onClose={() => {
            setShowUpload(false);
            setUploadAccession("");
          }}
          onUpload={handleUpload}
          uploading={uploading}
          prefillAccession={uploadAccession}
        />
      </Portal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ColumnSelectorPopover
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
        columns={IMAGING_COLUMNS}
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
        onResetColumns={handleResetColumns}
      />
    </Box>
  );
}
